import * as React from "react";
import { Trash2 } from "lucide-react";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { Localizer } from "@/main/react/Types";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { DeletedContentKind } from "@/main/react/page/profile-page/DeletedContentRestoreRow";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";

/**
 * The visible door to the author's own recovery list.
 *
 * <p>Added 2026-08-14, after the member this feature was built for deleted a poem by mistake, was
 * pointed at the recovery list, and could not find it. Nothing was broken: the row was intact, the
 * restore verb accepted it, and the deleted-tab query returned it FIRST. The prod ACCESS log showed he
 * loaded `/my_dashboard/discussions/authored` four times and `/my_dashboard/discussions/saved` once,
 * never `/my_dashboard/discussions/deleted`, with zero errors in the window. **The sub-tab select was
 * the only door and it does not look like one** (`DASHBOARD-6`).
 *
 * <p>Two properties are load-bearing and should survive any restyle:
 * <ul>
 *   <li><b>It is visible, not just named.</b> Giving the sub-tab select an `aria-label` landed in the
 *       same task and does not replace this: the member who cannot find the list is not usually the one
 *       using a screen reader, he is the one who cannot tell a pill is a control.</li>
 *   <li><b>It renders on the PROFILE too, owner-only.</b> "Mi perfil" is where a member goes looking for
 *       their own posts, and the recovery list lives on a different page entirely. The profile shows the
 *       door; the dashboard owns the list.</li>
 * </ul>
 *
 * <p>Reuses `dashboard.subtab.deleted`, already translated in all 25 catalogs, so this mints no copy.
 *
 * <p>Design: `designs/01-now/2026-08-12-author-recoverable-deleted-content-design.md`, 2026-08-14
 * addendum.
 */
/**
 * Which family of pills this one sits in, because its siblings differ by host (round 41 A4 / A39).
 *
 * <ul>
 *   <li>`"action-family"`: the dashboard identity card's Metrics / My profile / Edit profile row, all
 *       `CtaButton size="sm" variant="outline"` (`DASHBOARD-103`). Same recipe, so same size and weight.</li>
 *   <li>`"control-row"` (default): the profile's filter row beside the `SimpleSelect` pills, whose type
 *       scale is `text-xs sm:text-sm` (`PROFILE-77`).</li>
 * </ul>
 */
export type DeletedContentEntryPointPlacement = "action-family" | "control-row";

type Props = {
    kind: DeletedContentKind;
    localizer: Localizer;
    source: string;
    placement?: DeletedContentEntryPointPlacement;
};

export function deletedContentHref(kind: DeletedContentKind): string {
    return kind === "discussion"
        ? "/my_dashboard/discussions/deleted"
        : "/my_dashboard/arguments/deleted";
}

export function DeletedContentEntryPointButton({ kind, localizer, source, placement = "control-row" }: Props) {
    const href = deletedContentHref(kind);

    return (
        // `EMPTY-STATES-82` — this control computed a real address and threw it away. It is an anchor
        // carrying its destination, so a screen reader announces a link, and middle-click, Cmd/Ctrl-click,
        // hover preview and "copy link address" all work.
        //
        // Round 41 A4 (`DASHBOARD-103`): rendered through `CtaButton` (its `href` branch is the same
        // `Button asChild` + `<a>` this used to spell by hand), so on the dashboard it is BYTE-IDENTICAL
        // in class to the three action pills beside it. It used to be a bare `Button size="sm"`, whose
        // `text-sm` and 16px icon made the least-used pill the largest one in the row, and the longer
        // "Recently deleted" label (`DASHBOARD-132`) made that worse. On the profile (A39, `PROFILE-77`)
        // it takes the `SimpleSelect` pills' scale instead: `text-xs sm:text-sm`.
        //
        // The handler still runs on exactly the clicks it always did. `preventDefault()` is called
        // only for an unmodified primary click; a middle-click never reaches a React `onClick` at all,
        // and a modified click falls through to the browser with the real address. So the soft
        // navigation below is unchanged, `navigateToUrl` still owns it, and rule 33 still has nothing
        // to bind: this is a real navigation to a different page, not an in-page history push.
        <CtaButton
            href={href}
            icon={Trash2}
            text={localizer("dashboard.subtab.deleted")}
            size="sm"
            variant="outline"
            extraClass={placement === "control-row" ? "sm:text-sm" : ""}
            playwrightId="deleted-content-entry-point"
            data-deleted-content-kind={kind}
            data-deleted-content-placement={placement}
            onClick={(event) => {
                if (!isPageInterceptableClick(event)) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                navigateToUrl(href, { source });
            }}
        />
    );
}
